import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Space, Select, Input, Typography, Button, Tooltip, message, Popconfirm } from 'antd';
import {
  BellOutlined, ReloadOutlined, CheckOutlined, DeleteOutlined,
  BugOutlined, CloudServerOutlined, DatabaseOutlined, SearchOutlined,
  SafetyCertificateOutlined, InfoCircleOutlined, DashboardOutlined, DollarOutlined,
} from '@ant-design/icons';
import { systemAlertAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { brand, gray, state } from '../theme/tokens';
import { font } from '../theme';
import { EmptyState } from '../components/common';
import { useIsMobile } from '../hooks/useIsMobile';
import { useFitTableHeight } from '../hooks/useViewportFit';

const { Search } = Input;

/* 등급 — 색만으로 구분하지 않도록 라벨을 항상 문자로 적는다 */
export const ALERT_LEVELS: Record<string, { label: string; color: string; bg: string }> = {
  CRIT: { label: '심각', color: state.error,   bg: state.errorSoft },
  WARN: { label: '주의', color: state.warning, bg: state.warningSoft },
  INFO: { label: '정보', color: state.info,    bg: state.infoSoft },
};

/* 분류 — 발신 스크립트가 보내는 category 값 */
export const ALERT_CATEGORIES: Record<string, { label: string; icon: React.ReactNode }> = {
  ncpms:      { label: '병해충 예찰', icon: <BugOutlined /> },
  backup:     { label: '백업',        icon: <DatabaseOutlined /> },
  disk:       { label: '디스크',      icon: <CloudServerOutlined /> },
  opensearch: { label: '검색엔진',    icon: <SearchOutlined /> },
  inspection: { label: '정기점검',    icon: <SafetyCertificateOutlined /> },
  tapo:       { label: '온습도',      icon: <DashboardOutlined /> },
  oci:        { label: '클라우드 비용', icon: <DollarOutlined /> },
  etc:        { label: '기타',        icon: <InfoCircleOutlined /> },
};

export const alertCategoryMeta = (code: string) =>
  ALERT_CATEGORIES[code] || { label: code || '기타', icon: <InfoCircleOutlined /> };

/** 한 줄 미리보기 — Discord 원문의 장식용 구분선(━━━)·제목 중복 줄은 걷어낸다.
 *  (원문은 행을 펼치면 그대로 볼 수 있으므로 미리보기는 '내용'만 보여준다) */
export const alertPreview = (message: string, title = '', maxLines = 2) => {
  const norm = (v: string) => v.replace(/\s+/g, ' ').trim();
  const bare = (v: string) => norm(v).replace(/^[^\p{L}\p{N}]+/u, '').replace(/[^\p{L}\p{N}%)\]]+$/u, '');
  const t = bare(title);
  return (message || '')
    .split('\n')
    .map(norm)
    .filter(Boolean)
    // 구분선(━ ─ ═ = - _ · 반복)만 있는 줄 제거
    .filter(l => !/^[\s━─═＝=\-_·.~*]+$/.test(l))
    // Discord 머리글 '[제목]' 줄 제거 — 알림센터는 제목을 이미 따로 보여준다
    .filter(l => !/^[^\p{L}\p{N}]*\[[^\]]+\][^\p{L}\p{N}]*$/u.test(l))
    // 시각 줄 제거 — 목록에 '발생 시각' 칸이 따로 있다 ('발생 시각:'·'수집 시각:'·맨 시각)
    .filter(l => !/^[^\p{L}\p{N}]*[가-힣]{0,4}\s*시각\s*[:：]/u.test(l))
    .filter(l => !/^[^\p{L}\p{N}]*\d{4}-\d{2}-\d{2}[\s\d:.]*(KST|UTC)?$/u.test(l))
    // 제목과 같은 말만 반복하는 머리줄 제거 (이모지·괄호 차이는 무시)
    .filter(l => !(t && bare(l) === t))
    .slice(0, maxLines)
    .join(' · ');
};

const fmtTime = (v?: string) => (v || '').replace('T', ' ').slice(0, 16);

const LEVEL_OPTIONS = [
  { label: '전체 등급', value: '' },
  { label: '심각', value: 'CRIT' },
  { label: '주의', value: 'WARN' },
  { label: '정보', value: 'INFO' },
];

const READ_OPTIONS = [
  { label: '전체', value: 'all' },
  { label: '미읽음만', value: 'unread' },
];

const AlertCenterPage: React.FC = () => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const isMobile = useIsMobile();
  const { hasPermission } = usePermission();
  const canDelete = hasPermission('alerts', 'DELETE');

  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '', level: '', read: 'all', search: '', page: 1, page_size: 20,
  });

  const fetchData = useCallback(() => {
    setLoading(true);
    systemAlertAPI.list({
      category: filters.category || undefined,
      level: filters.level || undefined,
      unread_only: filters.read === 'unread' || undefined,
      search: filters.search || undefined,
      page: filters.page,
      page_size: filters.page_size,
    })
      .then(({ data }) => {
        setItems(data.items || []);
        setTotal(data.total || 0);
        setUnread(data.unread || 0);
        setCategories(data.categories || []);
      })
      .catch(() => message.error('알림 목록을 불러오지 못했습니다.'))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const markRead = async (id: number) => {
    try {
      await systemAlertAPI.read(id);
      // 목록을 다시 받지 않고 해당 행만 갱신 — 읽는 도중 표가 재정렬되지 않는다
      setItems(prev => prev.map(it => (it.id === id ? { ...it, is_read: true } : it)));
      setUnread(u => Math.max(0, u - 1));
    } catch { message.error('읽음 처리에 실패했습니다.'); }
  };

  const markAllRead = async () => {
    try {
      const { data } = await systemAlertAPI.readAll();
      message.success(`${data.marked || 0}건을 읽음 처리했습니다.`);
      fetchData();
    } catch { message.error('일괄 읽음 처리에 실패했습니다.'); }
  };

  const remove = async (id: number) => {
    try {
      await systemAlertAPI.remove(id);
      message.success('삭제되었습니다.');
      fetchData();
    } catch { message.error('삭제에 실패했습니다.'); }
  };

  const columns = [
    {
      title: '등급', dataIndex: 'level', key: 'level', width: 78,
      render: (v: string) => {
        const m = ALERT_LEVELS[v] || ALERT_LEVELS.INFO;
        return <Tag style={{ margin: 0, color: m.color, background: m.bg, borderColor: m.bg, fontWeight: 700 }}>{m.label}</Tag>;
      },
    },
    {
      title: '분류', dataIndex: 'category', key: 'category', width: 128, responsive: ['md'] as any, 
      render: (v: string) => {
        const m = alertCategoryMeta(v);
        return <Space size={5} style={{ color: gray[600], fontSize: 12 }}>{m.icon}{m.label}</Space>;
      },
    },
    {
      title: '알림 내용', dataIndex: 'title', key: 'title', ellipsis: true,
      render: (v: string, r: any) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: r.is_read ? 500 : 700, color: r.is_read ? gray[600] : gray[900], fontSize: 13 }}>
            {!r.is_read && <span style={{
              display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
              background: brand.primary, marginRight: 6, verticalAlign: 'middle',
            }} />}
            {v}
          </div>
          {r.message && (
            <div style={{
              fontSize: 11, color: gray[400], marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {alertPreview(r.message, v)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: '발생 시각', dataIndex: 'created_at', key: 'created_at', width: 136, responsive: ['md'] as any, 
      render: (v: string) => <span style={{ fontSize: 12, color: gray[500], fontVariantNumeric: 'tabular-nums' }}>{fmtTime(v)}</span>,
    },
    {
      title: '작업', key: 'actions', width: canDelete ? 88 : 56, responsive: ['md'] as any, 
      render: (_: any, r: any) => (
        <Space size={2}>
          {!r.is_read && (
            <Tooltip title="읽음 처리">
              <Button type="text" size="small" icon={<CheckOutlined />}
                style={{ color: brand.primary }}
                onClick={(e) => { e.stopPropagation(); markRead(r.id); }} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm title="이 알림을 삭제하시겠습니까?" okText="삭제" cancelText="취소"
              onConfirm={() => remove(r.id)}>
              <Tooltip title="삭제">
                <Button type="text" size="small" danger icon={<DeleteOutlined />}
                  onClick={(e) => e.stopPropagation()} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div ref={cardWrapRef}>
      <Card
        bodyStyle={{ padding: '12px 16px 0' }}
        title={
          <Space>
            <BellOutlined style={{ color: brand.primary }} />
            <span>알림센터</span>
            <Typography.Text type="secondary" style={{ fontSize: 13, fontWeight: 400 }}>
              총 {total}건{unread > 0 && ` · 미읽음 ${unread}건`}
            </Typography.Text>
          </Space>
        }
        extra={
          <Space>
            <Button size="small" icon={<CheckOutlined />} onClick={markAllRead} disabled={unread === 0}>
              모두 읽음
            </Button>
            <Tooltip title="새로고침">
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchData} />
            </Tooltip>
          </Space>
        }
      >
        {/* 필터 바 */}
        <Space size={8} wrap style={{ marginBottom: 10 }}>
          <Select
            size="small" style={{ width: 130 }}
            value={filters.category}
            onChange={v => setFilters(p => ({ ...p, category: v, page: 1 }))}
            options={[{ label: '전체 분류', value: '' },
              ...categories.map(c => ({ label: alertCategoryMeta(c).label, value: c }))]}
          />
          <Select
            size="small" style={{ width: 110 }}
            value={filters.level}
            onChange={v => setFilters(p => ({ ...p, level: v, page: 1 }))}
            options={LEVEL_OPTIONS}
          />
          <Select
            size="small" style={{ width: 110 }}
            value={filters.read}
            onChange={v => setFilters(p => ({ ...p, read: v, page: 1 }))}
            options={READ_OPTIONS}
          />
          <Search
            placeholder="제목·내용 검색" allowClear size="small" style={{ width: 240 }}
            onSearch={v => setFilters(p => ({ ...p, search: v, page: 1 }))}
          />
        </Space>

        <style>{`
          .alert-row-unread td:first-child { border-left: 3px solid ${brand.primary} !important; }
          .alert-row-CRIT td:first-child { border-left: 3px solid ${state.error} !important; }
          .alert-msg { margin: 0; padding: 10px 12px; background: ${gray[50]};
            border: 1px solid ${gray[200]}; border-radius: 8px; white-space: pre-wrap;
            font-family: ${font.mono}; font-size: 12px; line-height: 1.7; color: ${gray[700]}; }
        `}</style>

        <div ref={tableWrapRef}>
          <Table
            dataSource={items}
            columns={columns}
            rowKey="id"
            loading={loading}
            size="small"
            scroll={{ x: isMobile ? undefined : 'max-content', y: bodyHeight }}
            locale={{ emptyText: <EmptyState title="알림이 없습니다" size="sm" /> }}
            rowClassName={(r: any) => (r.level === 'CRIT' ? 'alert-row-CRIT' : (r.is_read ? '' : 'alert-row-unread'))}
            expandable={{
              // 행을 펼치면 Discord 로 나간 원문 그대로 보여준다
              expandedRowRender: (r: any) => (
                <div>
                  <pre className="alert-msg">{r.message || '(내용 없음)'}</pre>
                  <div style={{ marginTop: 6, fontSize: 11, color: gray[400] }}>
                    발신 {r.source || '-'} · {fmtTime(r.created_at)}
                  </div>
                </div>
              ),
              rowExpandable: (r: any) => Boolean(r.message),
              onExpand: (expanded: boolean, r: any) => { if (expanded && !r.is_read) markRead(r.id); },
            }}
            pagination={{
              current: filters.page,
              pageSize: filters.page_size,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '200'],
              showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건`,
              size: 'small',
              onChange: (page, pageSize) => setFilters(p => ({ ...p, page, page_size: pageSize })),
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default AlertCenterPage;
